import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartTotal, getCartCount } = useCart();
  const total = getCartTotal();
  const count = getCartCount();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen section-spacing">
        <div className="container-handcraft text-center">
          <div className="max-w-md mx-auto">
            <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-10 h-10 text-muted-foreground" />
            </div>
            <p className="font-handwritten text-3xl text-accent mb-4">Oops!</p>
            <h1 className="font-serif text-3xl text-charcoal mb-4">Your cart is empty</h1>
            <p className="text-muted-foreground mb-8">
              Looks like you haven't added any items to your cart yet.
            </p>
            <Link to="/shop">
              <Button className="btn-handcraft bg-charcoal text-cream" data-testid="continue-shopping">
                Continue Shopping
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-sand">
        <div className="container-handcraft">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-charcoal transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-charcoal">Cart</span>
          </nav>
        </div>
      </div>

      <section className="section-spacing">
        <div className="container-handcraft">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <p className="font-handwritten text-2xl text-accent mb-2">Your Selection</p>
              <h1 className="font-serif text-3xl md:text-4xl text-charcoal">Shopping Cart</h1>
            </div>
            <p className="text-muted-foreground">{count} {count === 1 ? 'item' : 'items'}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cartItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="flex gap-4 md:gap-6 p-4 md:p-6 bg-white rounded-2xl border border-sand"
                  data-testid={`cart-item-${index}`}
                >
                  {/* Image */}
                  <Link to={`/product/${item.product.slug}`} className="flex-shrink-0">
                    <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden bg-muted">
                      <img
                        src={item.product.images?.[0] || 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400'}
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </Link>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.product.slug}`}>
                      <h3 className="font-serif text-lg md:text-xl text-charcoal hover:text-accent transition-colors">
                        {item.product.name}
                      </h3>
                    </Link>
                    
                    <p className="text-sm text-muted-foreground mt-1">
                      ₹{(item.product.sale_price || item.product.price).toLocaleString()} each
                    </p>

                    {/* Custom Order Details */}
                    {item.customOptions?.isCustom && (
                      <div className="mt-2 p-3 bg-sage/10 rounded-lg text-sm">
                        <span className="badge-handcraft bg-sage text-white text-xs mb-2">Custom Order</span>
                        {item.customOptions.notes && (
                          <p className="text-muted-foreground mt-2">
                            <span className="font-medium">Notes:</span> {item.customOptions.notes}
                          </p>
                        )}
                        {item.customOptions.preferences && (
                          <p className="text-muted-foreground">
                            <span className="font-medium">Preferences:</span> {item.customOptions.preferences}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between mt-4">
                      <div className="quantity-input">
                        <button 
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          data-testid={`cart-minus-${index}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <input type="number" value={item.quantity} readOnly />
                        <button 
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          data-testid={`cart-plus-${index}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>

                      <button
                        onClick={() => removeFromCart(index)}
                        className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                        data-testid={`cart-remove-${index}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-right">
                    <p className="font-semibold text-charcoal">
                      ₹{((item.product.sale_price || item.product.price) * item.quantity).toLocaleString()}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white rounded-2xl border border-sand p-6 md:p-8">
                <h2 className="font-serif text-2xl text-charcoal mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>₹{total.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Shipping</span>
                    <span className="text-sage">Free</span>
                  </div>
                </div>

                <div className="border-t border-sand pt-4 mb-6">
                  <div className="flex justify-between text-lg font-semibold text-charcoal">
                    <span>Total</span>
                    <span>₹{total.toLocaleString()}</span>
                  </div>
                </div>

                <Button
                  onClick={() => navigate('/checkout')}
                  className="w-full btn-handcraft bg-charcoal text-cream hover:bg-charcoal/90"
                  data-testid="proceed-to-checkout"
                >
                  Proceed to Checkout
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <Link to="/shop" className="block text-center mt-4 text-sm text-muted-foreground hover:text-charcoal transition-colors">
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CartPage;
